import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { SvgIconProps } from '@mui/material/SvgIcon';

// Icons
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import StorefrontIcon from '@mui/icons-material/Storefront';
import WorkIcon from '@mui/icons-material/Work';
import ArticleIcon from '@mui/icons-material/Article';
import NoteIcon from '@mui/icons-material/Note';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import LogoutIcon from '@mui/icons-material/Logout';

import { ErrorAlert } from '../components/Alerts';
import Loading from '../components/Loading';
import apiService from '../services/api';
import liffService from '../services/liff';
import { User, FAQ } from '../types';
import { useAuth } from '../auth/AuthContext';

const UserHelp: React.FC = () => {
  const [user, setUser] = useState<User>();
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  // 退会成功時は終端表示に切り替えるため、成功アラートは使わない
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [userToDelete, setUserToDelete] = useState<User>();
  // 退会完了後は終端表示に固定する（所属が全て無効化されるため他画面は空になる）
  const [withdrawn, setWithdrawn] = useState<boolean>(false);
  const { userId } = useAuth();

  // アイコンマッピング
  const iconMap: { [key: string]: React.ComponentType<SvgIconProps> } = {
    'HelpOutline': HelpOutlineIcon,
    'Storefront': StorefrontIcon,
    'Work': WorkIcon,
    'Article': ArticleIcon,
    'Note': NoteIcon,
    'LinkOff': LinkOffIcon,
    'Logout': LogoutIcon,
  };

  // ユーザー情報とFAQを取得
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // ユーザー情報を取得
        const userResponse = await apiService.getUser(userId);
        if (userResponse.success && userResponse.data) {
          setUser(userResponse.data);
        }
        
        // FAQを取得
        const faqResponse = await apiService.getFAQs();
        if (faqResponse.success && faqResponse.data) {
          // 有効なFAQのみを表示順でソート
          const activeFaqs = faqResponse.data
            .filter(faq => faq.is_active)
            .sort((a, b) => a.display_order - b.display_order);
          setFaqs(activeFaqs);
        }
      } catch (err) {
        setError('データの取得に失敗しました。');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [userId]);

  // 削除確認ダイアログを表示する関数
  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
    setUserToDelete(user);  
  };

  // 削除確認ダイアログを閉じる関数
  const handleCloseDialog = () => {
    setDeleteDialogOpen(false);
  };

  // ユーザーを削除する関数
  const handleDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      setLoading(true);
      const response = await apiService.deleteUsers(userId);

      if (!response.error) {
        // 退会後は他の画面を触らせない（所属が全て無効化され、どの画面も空になるため）。
        // LINE内ブラウザでは自動で閉じ、外部ブラウザでは終端表示のままにする。
        setWithdrawn(true);
        if (liffService.isInClient()) {
          liffService.closeWindow();
        }
      } else {
        setError('退会処理に失敗しました。もう一度お試しください。');
      }
    } catch (err) {
      setError('退会処理に失敗しました。もう一度お試しください。');
    } finally {
      setLoading(false);
      handleCloseDialog();
    }
  };

  if (loading) return <Loading />;

  // 退会完了の終端表示。再開できることを明示する（退会は論理削除でデータは残る）。
  if (withdrawn) {
    return (
      <Container maxWidth="sm" sx={{ py: 6, textAlign: 'center' }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
          退会が完了しました
        </Typography>
        <Typography variant="body2" sx={{ color: '#757575' }}>
          ご利用ありがとうございました。<br />
          この画面は閉じてお戻りください。
        </Typography>
        <Typography variant="body2" sx={{ color: '#757575', mt: 2 }}>
          再度ご利用になる場合は、店舗のリンクからアクセスし
          「利用を再開する」を選んでください。
        </Typography>
      </Container>
    );
  }

  return(
    <Box sx={{ pt: 0, px: 0, pb: 2, m: 0 }}>
      {error && <ErrorAlert message={error} />}

      {/* ヘッダー部分 */}
      <Box
        sx={{
          p: { xs: 2, sm: 3 },
          mb: 2,
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          component={RouterLink}
          to='/mypage'
          sx={{
            fontSize: { xs: '0.75rem', sm: '0.875rem' },
            color: '#9e9d9d',
            mb: 1,
          }}
        >
          マイページにもどる
        </Button>
      </Box>
      
      {/* メインコンテンツ */}
      <Container maxWidth="md">
        <Typography variant="h5" component="h1" sx={{
          fontWeight: 'fontWeightBold',
          fontSize: '1.25rem',
          textAlign: 'center',
          mb: 3,
        }}>
          よくある質問
        </Typography>
        
        {/* FAQアコーディオン */}
        <Box sx={{ mb: 4 }}>
          {faqs.map((faq) => {
            const IconComponent = iconMap[faq.icon_name] || HelpOutlineIcon;
            
            return (
              <Accordion key={faq.id} sx={{ mb: 1 }}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls={`panel-${faq.id}-content`}
                  id={`panel-${faq.id}-header`}
                  sx={{ 
                    '&.Mui-expanded': {
                      backgroundColor: 'rgba(0, 0, 0, 0.03)'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ mr: 1.5 }}>
                      <IconComponent color="primary" />
                    </Box>
                    <Typography sx={{ fontWeight: 'medium' }}>
                      {faq.question}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 1, pb: 2 }}>
                  <Typography>
                    {faq.answer}
                  </Typography>
                  
                  {faq.has_action && (
                    <Box sx={{ mt: 2 }}>
                      <Button 
                        variant="contained" 
                        color="error"
                        size="small"
                        startIcon={<LogoutIcon />}
                        onClick={handleDeleteClick}
                      >
                        Re:crewを退会する
                      </Button>
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      </Container>

      {/* 削除確認ダイアログ */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          退会の確認
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {user && `「${user.name}」さん、本当に退会してもよろしいですか？`}
            <Box sx={{ mt: 1 }}>
              退会すると、ご利用履歴などの一切の情報について、内容の確認ができなくなります。
            </Box>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            キャンセル
          </Button>
          <Button onClick={handleDeleteUser} color="error" autoFocus>
            退会する
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserHelp;